import React, { useState, useRef } from "react";
import { Button, StyledModal } from "@bikdotai/bik-component-library";
import { getDownloadURL } from "firebase/storage";
import UploadIcon from "../../../icons/UploadIcon";
import { FirebaseService } from "../../../services/firebase/FirebaseService";
import { voidFunction } from "../../../app/messageBlock/Constants";
import { useAppSelector } from "../../../state/store";
import {
  WhatsNewBulkUploadService,
  BulkUploadArticlesResult,
} from "../services/whatsNewBulkUpload.service";
import { downloadWhatsNewTemplate } from "../services/whatsNewTemplate";
import { parseWhatsNewFileForPreview } from "../services/whatsNewClientParse";
import { WhatsNewArticle } from "../types/whatsNew.types";

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Existing articles, used to flag duplicate titles before uploading. */
  existingArticles?: WhatsNewArticle[];
}

interface FilePreview {
  totalCount: number;
  duplicates: string[];
  file: File;
}

const PURPLE = "#731DCF";

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  existingArticles = [],
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] =
    useState<BulkUploadArticlesResult | null>(null);
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const homeState = useAppSelector((state) => state.homeState);
  const firebaseService = new FirebaseService(voidFunction, voidFunction);
  const service = new WhatsNewBulkUploadService();

  const getMediaPath = (fileName: string) => {
    const extension = fileName.split(".").pop();
    const timestamp = new Date().getTime();
    return `chatbot/${homeState.storeId || "admin"}/whats-new/bulk-uploads/${timestamp}.${extension}`;
  };

  const uploadFileToFirebase = async (file: File): Promise<string> => {
    const uploadRef = firebaseService.getUploadRef(
      file,
      getMediaPath(file.name)
    );

    return new Promise<string>((resolve, reject) => {
      uploadRef.on(
        "state_changed",
        (snapshot) => {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(percent);
        },
        (error) => {
          console.error("Error uploading file:", error);
          reject(error);
        },
        () => {
          getDownloadURL(uploadRef.snapshot.ref).then(resolve).catch(reject);
        }
      );
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = [".xlsx", ".xls", ".csv"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      alert("Please select an Excel (.xlsx, .xls) or CSV file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    // Step 1: parse client-side for a preview (row count + duplicate titles)
    // before uploading, mirroring the Service Features flow.
    setIsParsing(true);
    setUploadStatus(null);
    setErrorMessage(null);
    try {
      const { totalRows, titles } = await parseWhatsNewFileForPreview(file);

      const existingTitles = new Set(
        existingArticles.map((a) => (a.title || "").trim().toLowerCase())
      );
      const seen = new Set<string>();
      const duplicates: string[] = [];
      for (const title of titles) {
        const key = title.toLowerCase();
        if (existingTitles.has(key) && !seen.has(key)) {
          seen.add(key);
          duplicates.push(title);
        }
      }

      setFilePreview({ totalCount: totalRows, duplicates, file });
    } catch (error: any) {
      console.error("Error parsing file:", error);
      setErrorMessage(
        error?.message ||
          "We couldn't read this file. Please check the format and try again."
      );
    } finally {
      setIsParsing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirmUpload = async () => {
    if (!filePreview) return;
    const { file } = filePreview;

    setFilePreview(null);
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);
    setErrorMessage(null);

    try {
      const firebaseUrl = await uploadFileToFirebase(file);
      const result = await service.bulkUploadArticles(firebaseUrl);
      setUploadStatus(result.response);

      // NOTE: the list refresh (onSuccess) is deferred to handleClose. Calling
      // it here would trigger the panel's `loading` guard, unmount this modal,
      // and wipe the summary the user needs to read.

      // Auto-close only on a fully clean upload; otherwise keep the summary
      // (with the per-row error list) visible for the user to review.
      if (result.response.errorCount === 0 && result.response.successCount > 0) {
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error: any) {
      // Show the failure inside the modal so the user always sees an outcome
      // after uploading (e.g. empty file, or the whole upload failing).
      console.error("Error in bulk upload:", error);
      setErrorMessage(
        error?.message ||
          "We couldn't process this file. Please check the format and try again."
      );
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (isUploading || isParsing) return;
    // Refresh the list on close so newly created articles appear (no page
    // refresh). Deferred to here to avoid unmounting the modal mid-summary.
    const createdSomething = (uploadStatus?.successCount ?? 0) > 0;
    setUploadStatus(null);
    setErrorMessage(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (createdSomething) {
      onSuccess();
    }
    onClose();
  };

  const handleCancelPreview = () => {
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRetry = () => {
    setErrorMessage(null);
    setUploadStatus(null);
    setFilePreview(null);
  };

  const handleDownloadErrorFile = () => {
    if (uploadStatus?.errorFileUrl) {
      window.open(uploadStatus.errorFileUrl, "_blank");
    }
  };

  const renderUploading = () => (
    <div style={{ padding: "40px 24px", textAlign: "center" }}>
      <div
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "50%",
          width: "80px",
          height: "80px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px auto",
        }}
      >
        <UploadIcon width="40" height="40" fill={PURPLE} />
      </div>
      <h3
        style={{
          margin: "0 0 8px 0",
          fontSize: "18px",
          fontWeight: 600,
          color: "#1f2937",
        }}
      >
        Uploading and processing file...
      </h3>
      <p style={{ margin: "0 0 32px 0", color: "#6b7280", fontSize: "14px" }}>
        Please wait while we upload and validate your articles.
      </p>
      <div
        style={{
          width: "100%",
          height: "8px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${uploadProgress}%`,
            height: "100%",
            backgroundColor: PURPLE,
            transition: "width 0.15s ease",
          }}
        />
      </div>
    </div>
  );

  const renderParsing = () => (
    <div style={{ padding: "40px 24px", textAlign: "center" }}>
      <div
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "50%",
          width: "80px",
          height: "80px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px auto",
        }}
      >
        <UploadIcon width="40" height="40" fill={PURPLE} />
      </div>
      <h3
        style={{
          margin: "0 0 8px 0",
          fontSize: "18px",
          fontWeight: 600,
          color: "#1f2937",
        }}
      >
        Reading file...
      </h3>
      <p style={{ margin: 0, color: "#6b7280", fontSize: "14px" }}>
        Checking rows and looking for duplicates.
      </p>
    </div>
  );

  const renderPreview = (preview: FilePreview) => (
    <div>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#f0f9ff",
          borderRadius: "8px",
          marginBottom: "16px",
        }}
      >
        <h3
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#1f2937",
          }}
        >
          File Preview
        </h3>
        <div style={{ marginBottom: "8px" }}>
          <span style={{ color: "#1f2937", fontWeight: 500 }}>
            Total rows to upload: {preview.totalCount}
          </span>
        </div>

        {preview.duplicates.length > 0 ? (
          <div style={{ marginTop: "12px" }}>
            <p style={{ fontWeight: 500, margin: "0 0 8px 0", color: "#dc2626" }}>
              ⚠️ {preview.duplicates.length} title(s) already exist. Uploading
              will create additional articles with the same title.
            </p>
            <div
              style={{
                maxHeight: "180px",
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                backgroundColor: "white",
              }}
            >
              <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>
                      Duplicate title
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {preview.duplicates.map((title, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px" }}>{title}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: "8px" }}>
            <span style={{ color: "#059669", fontSize: "14px" }}>
              ✓ No duplicate titles found
            </span>
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        <Button
          buttonText="Cancel"
          onClick={handleCancelPreview}
          size="medium"
          buttonType="secondary"
        />
        <Button
          buttonText="Confirm & Upload"
          onClick={handleConfirmUpload}
          size="medium"
        />
      </div>
    </div>
  );

  const renderError = (message: string) => (
    <div>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: "8px",
          marginBottom: "16px",
        }}
      >
        <h3
          style={{
            margin: "0 0 8px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#991b1b",
          }}
        >
          Upload failed
        </h3>
        <p style={{ margin: 0, fontSize: "14px", color: "#b91c1c" }}>{message}</p>
        <p style={{ margin: "8px 0 0 0", fontSize: "13px", color: "#6b7280" }}>
          No articles were created. Fix the file and try again.
        </p>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        <Button
          buttonText="Close"
          onClick={handleClose}
          size="medium"
          buttonType="secondary"
        />
        <Button buttonText="Try again" onClick={handleRetry} size="medium" />
      </div>
    </div>
  );

  const renderResults = (status: BulkUploadArticlesResult) => (
    <div>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#f0f9ff",
          borderRadius: "8px",
          marginBottom: "16px",
        }}
      >
        <h3
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#1f2937",
          }}
        >
          Upload Complete
        </h3>
        <div style={{ marginBottom: status.errorCount > 0 ? "12px" : 0 }}>
          <span style={{ color: "#059669", fontWeight: 600 }}>
            {status.successCount} article{status.successCount === 1 ? "" : "s"} created
          </span>
          <span style={{ color: "#6b7280", fontWeight: 600 }}> · </span>
          <span
            style={{
              color: status.errorCount > 0 ? "#dc2626" : "#6b7280",
              fontWeight: 600,
            }}
          >
            {status.errorCount} row{status.errorCount === 1 ? "" : "s"} failed
          </span>
        </div>
        {status.errorRows && status.errorRows.length > 0 && (
          <p style={{ margin: "0 0 4px 0", fontSize: "13px", color: "#6b7280" }}>
            Failed rows (row number and reason):
          </p>
        )}

        {status.errorRows && status.errorRows.length > 0 && (
          <div style={{ marginTop: "12px" }}>
            <div
              style={{
                maxHeight: "220px",
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                backgroundColor: "white",
              }}
            >
              <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>
                      Row
                    </th>
                    <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>
                      Errors
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {status.errorRows.map((err) => (
                    <tr key={err.rowIndex} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px", fontFamily: "monospace", verticalAlign: "top" }}>
                        {err.rowIndex}
                      </td>
                      <td style={{ padding: "8px", color: "#dc2626" }}>
                        {err.errors.join("; ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {status.errorFileUrl && (
          <div style={{ marginTop: "12px" }}>
            <Button
              buttonText="Download Error Report"
              onClick={handleDownloadErrorFile}
              size="medium"
              style={{ backgroundColor: "#dc2626", color: "white", border: "none" }}
            />
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        <Button buttonText="Close" onClick={handleClose} size="medium" />
      </div>
    </div>
  );

  const renderUploadPrompt = () => (
    <div>
      <div
        style={{
          border: "2px dashed #d1d5db",
          borderRadius: "12px",
          padding: "32px 24px",
          textAlign: "center",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            backgroundColor: "#f5f5f5",
            borderRadius: "50%",
            width: "80px",
            height: "80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px auto",
          }}
        >
          <UploadIcon width="40" height="40" fill={PURPLE} />
        </div>
        <h3
          style={{
            margin: "0 0 8px 0",
            fontSize: "18px",
            fontWeight: 600,
            color: "#1f2937",
          }}
        >
          Upload Excel/CSV File
        </h3>
        <p style={{ margin: "0 0 24px 0", color: "#6b7280", fontSize: "14px" }}>
          Excel (.xlsx, .xls) or CSV format • Max 10MB
        </p>
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `1px solid ${PURPLE}`,
            borderRadius: "8px",
            padding: "12px 16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          <UploadIcon width="20" height="20" fill={PURPLE} />
          <span style={{ fontSize: "14px", fontWeight: 500, color: PURPLE }}>
            Choose File
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Button
          buttonText="Download Template"
          onClick={downloadWhatsNewTemplate}
          size="medium"
          style={{ color: PURPLE, border: `1px solid ${PURPLE}` }}
        />
        <Button
          buttonText="Cancel"
          onClick={handleClose}
          size="medium"
          style={{ color: "#374151", border: "1px solid #d1d5db" }}
        />
      </div>
    </div>
  );

  return (
    <StyledModal
      open={isOpen}
      headingTitle="Bulk Upload Articles"
      onClose={handleClose}
      width="600px"
    >
      <div style={{ padding: "24px" }}>
        {isUploading
          ? renderUploading()
          : isParsing
          ? renderParsing()
          : errorMessage
          ? renderError(errorMessage)
          : uploadStatus
          ? renderResults(uploadStatus)
          : filePreview
          ? renderPreview(filePreview)
          : renderUploadPrompt()}
      </div>
    </StyledModal>
  );
};

export default BulkUploadModal;
